import type { ReactNode } from "react"
import { memo, useMemo } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { Link } from "@tanstack/react-router"
import { IconLink3Outline18 } from "nucleo-ui-outline-18"

import type { AgentRecommendation } from "@productlane/zero/schema"
import { cn } from "@productlane/lib/src/browser"
import { formatRelativeTime } from "@productlane/lib/src/date"
import { queries } from "@productlane/zero/queries"

import type { ClosedGroup, DecidedRow } from "./decided-items"
import { api } from "@/lib/api"
import { decidedRows } from "./decided-items"
import { FADE_OUT_LABEL } from "./idea-list"
import { RecommendationTypeIcon } from "./recommendation-type-icon"
import { useOpenAgentRecommendations } from "./use-agent-recommendations"

const ROW = "flex h-10 items-center gap-3 rounded-lg px-3 text-sm text-primary"

const RowBody = memo(
	({ row, compact }: { row: DecidedRow; compact: boolean }) => (
		<>
			<RecommendationTypeIcon type={row.type} />
			<span className={FADE_OUT_LABEL}>{row.title}</span>
			{!compact && (
				<span className="hidden flex-none text-xs text-tertiary sm:block">
					{row.reason ?? row.meta}
				</span>
			)}
			<span className="w-16 flex-none text-right text-xs tabular-nums text-tertiary">
				{formatRelativeTime(new Date(row.at))}
			</span>
		</>
	),
)

/** What the team already accepted or declined, newest first. */
export const DecidedList = memo(
	({
		tab,
		selectedId = null,
		compact = false,
	}: {
		tab: "accepted" | "declined"
		selectedId?: string | null
		// The narrow column beside an open card: title and date only.
		compact?: boolean
	}) => {
		const [accepted] = useQuery(
			queries.closedAgentRecommendations({ status: "ACCEPTED" }),
		)
		const [declined] = useQuery(
			queries.closedAgentRecommendations({ status: "DISMISSED" }),
		)
		const [groups] = useQuery(queries.closedProposalGroups())
		const { rows: open } = useOpenAgentRecommendations()
		const rows = useMemo(
			() =>
				decidedRows({
					tab,
					recommendations: (tab === "accepted"
						? accepted
						: declined) as readonly AgentRecommendation[],
					otherRecommendations: (tab === "accepted"
						? declined
						: accepted) as readonly AgentRecommendation[],
					openGroupOwners: new Set(
						open
							.map((r) => r.proposalGroupId)
							.filter((id): id is string => !!id),
					),
					groups: groups as unknown as readonly ClosedGroup[],
				}),
			[tab, accepted, declined, groups, open],
		)

		if (rows.length === 0) {
			return (
				<p className="px-3 py-2 text-sm text-tertiary">
					{tab === "accepted"
						? "Nothing accepted yet."
						: "Nothing declined yet."}
				</p>
			)
		}
		return (
			<ul
				data-testid="agent-decided-list"
				className="flex list-none flex-col gap-px"
			>
				{rows.map((row) => (
					<li key={row.id}>
						{row.href ? (
							<Link
								to={row.href}
								aria-current={row.id === selectedId ? "true" : undefined}
								className={cn(
									ROW,
									row.id === selectedId
										? "bg-gray-150 dark:bg-gray-850"
										: "hover:bg-gray-100 dark:hover:bg-gray-800",
								)}
							>
								<RowBody row={row} compact={compact} />
							</Link>
						) : (
							<div className={ROW}>
								<RowBody row={row} compact={compact} />
							</div>
						)}
					</li>
				))}
			</ul>
		)
	},
)

/** A heading over the decided cards, so they never read as still open. */
export const DecidedSection = memo(
	({ label, children }: { label: string; children: ReactNode }) => (
		<section className="flex flex-col gap-1 pt-4">
			<h3 className="px-3 text-xs font-medium text-tertiary">{label}</h3>
			{children}
		</section>
	),
)

/** Customer reports the agent attached to an issue the team already tracks, newest first. */
export const LinkedList = memo(({ compact = false }: { compact?: boolean }) => {
	const links = api.workspace.getAgentIssueLinks.useQuery(
		{},
		{ staleTime: 60_000 },
	)
	if (links.isLoading) return null
	const rows = links.data ?? []
	if (rows.length === 0) {
		return (
			<p className="px-3 py-2 text-sm text-tertiary">Nothing linked yet.</p>
		)
	}
	return (
		<ul className="flex list-none flex-col gap-px">
			{rows.map((row) => (
				<li key={row.id}>
					<Link
						to="/threads/$id"
						params={{ id: row.threadId }}
						className={cn(ROW, "hover:bg-gray-100 dark:hover:bg-gray-800")}
					>
						<IconLink3Outline18 className="h-4 w-4 flex-none text-secondary" />
						<span className="flex-none tabular-nums text-tertiary">
							{row.identifier}
						</span>
						<span className={FADE_OUT_LABEL}>
							{row.issueTitle ?? row.threadTitle}
						</span>
						{!compact && (
							<span className="hidden max-w-60 flex-none truncate text-xs text-tertiary sm:block">
								{row.statusName ?? row.threadTitle}
							</span>
						)}
						<span className="w-16 flex-none text-right text-xs tabular-nums text-tertiary">
							{formatRelativeTime(new Date(row.linkedAt))}
						</span>
					</Link>
				</li>
			))}
		</ul>
	)
})
